import {
  decodeSpecCell,
  encodeSpecCell,
  isFileSizeProperty,
  type SpecCellError,
  type SpecMargins,
  type SpecRawValue,
  type SpecSize,
  type SpecValueKind,
} from '@inference.engine/ad-formats/web';

/* eslint-disable i18next/no-literal-string -- property keys and editor names, not copy */
const LIST_PROPERTIES: ReadonlySet<string> = new Set(['codec', 'audio.codec']);
export const SIDES = ['top', 'right', 'bottom', 'left'] as const;
/* eslint-enable i18next/no-literal-string */

export type Side = (typeof SIDES)[number];

/** Which inputs the sheet shows for a property. */
export type SpecEditor =
  | 'size'
  | 'margins'
  | 'transparency'
  | 'fileSize'
  | 'list'
  | 'commaList'
  | 'text';

export function specEditor(kind: SpecValueKind, property: string): SpecEditor {
  switch (kind) {
    case 'size':
      return 'size';
    case 'margins':
      return 'margins';
    case 'transparency':
      return 'transparency';
    case 'number':
      return isFileSizeProperty(property) ? 'fileSize' : 'text';
    case 'stringList':
      return LIST_PROPERTIES.has(property) ? 'list' : 'commaList';
    default:
      return 'text';
  }
}

/** Everything the sheet's inputs hold, as typed. Only the fields of the property's editor are read. */
export interface ValueDraft {
  text: string;
  width: string;
  height: string;
  margins: Record<Side, string>;
  items: string[];
}

export function emptyDraft(): ValueDraft {
  return {
    text: '',
    width: '',
    height: '',
    margins: { top: '', right: '', bottom: '', left: '' },
    items: [],
  };
}

export function draftFromValue(
  kind: SpecValueKind,
  property: string,
  value: SpecRawValue | null,
): ValueDraft {
  const draft = emptyDraft();
  if (value === null) return draft;

  switch (specEditor(kind, property)) {
    case 'size': {
      const size = value as SpecSize;
      return {
        ...draft,
        width: size.width?.toString() ?? '',
        height: size.height?.toString() ?? '',
      };
    }
    case 'margins': {
      const margins = value as SpecMargins;
      return {
        ...draft,
        margins: Object.fromEntries(SIDES.map((side) => [side, String(margins[side])])) as Record<
          Side,
          string
        >,
      };
    }
    case 'list':
      return { ...draft, items: [...(value as readonly string[])] };
    case 'commaList':
      return { ...draft, text: (value as readonly string[]).join(', ') };
    case 'transparency':
      return { ...draft, text: value as string };
    case 'fileSize':
    case 'text':
      return { ...draft, text: encodeSpecCell(kind, value, property) };
  }
}

export type DraftError =
  | SpecCellError
  | 'value_required'
  | 'size_side'
  | 'size_required'
  | 'margin_side'
  | 'list_required';

export type DecodedDraft = { value: SpecRawValue | null } | { error: DraftError };

/** Empty means not given; anything but whole pixels is invalid. */
function parsePixels(cell: string): number | null | undefined {
  const trimmed = cell.trim();
  if (!trimmed) return null;
  return /^\d+$/.test(trimmed) ? Number(trimmed) : undefined;
}

/** The typed inputs as a value in the property's stored shape, or why they are not one yet. */
export function decodeDraft(
  kind: SpecValueKind,
  property: string,
  draft: ValueDraft,
): DecodedDraft {
  const text = draft.text.trim();

  switch (specEditor(kind, property)) {
    case 'size': {
      const width = parsePixels(draft.width);
      const height = parsePixels(draft.height);
      if (width === undefined || height === undefined) return { error: 'size_side' };
      if (width === null && height === null) return { error: 'size_required' };
      return { value: { width, height } };
    }
    case 'margins': {
      const sides = SIDES.map((side) => parsePixels(draft.margins[side]));
      if (sides.some((side) => side === undefined)) return { error: 'margin_side' };
      const [top, right, bottom, left] = sides.map((side) => side ?? 0);
      return { value: { top, right, bottom, left, unit: 'px' } };
    }
    case 'list':
      return draft.items.length > 0 ? { value: draft.items } : { error: 'list_required' };
    case 'commaList': {
      const items = text
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      return items.length > 0 ? { value: items } : { error: 'value_required' };
    }
    case 'transparency':
      return text ? { value: text } : { error: 'value_required' };
    case 'fileSize':
    case 'text':
      return text ? decodeSpecCell(kind, text, property) : { error: 'value_required' };
  }
}
